// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Anchor, Button, Card, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import type { Appointment, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import { IconCalendarCheck } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AppointmentTimeWithTimezone } from '../components/appointments/AppointmentTimeWithTimezone';
import { BookingConfirmation } from '../components/appointments/BookingConfirmation';
import { SlotPicker } from '../components/appointments/SlotPicker';
import { useAppointmentTypes } from '../hooks/useAppointmentTypes';
import type { PatientAppointmentCandidate } from '../scheduling-next/types';
import { reschedulePatientV2Appointment } from '../services/appointment-lifecycle-service';
import { formatAppointmentDate } from '../utils/appointment-formatting';
import { ensurePatientTimezone } from '../utils/patient-timezone';

/**
 * Reschedule page for patient self-scheduling.
 * Loads the existing appointment, shows a SlotPicker for the same type,
 * and on confirmation calls rescheduleAppointment to atomically book-new then cancel-old.
 * @returns The reschedule page component
 */
export function ReschedulePage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { appointmentTypes, loading: typesLoading } = useAppointmentTypes();

  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const [appointmentType, setAppointmentType] = useState<AppointmentTypeDefinition | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const [selectedCandidate, setSelectedCandidate] = useState<PatientAppointmentCandidate | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const loadAppointment = useCallback(async () => {
    if (!appointmentId) {
      setError('No appointment ID provided.');
      setLoading(false);
      return;
    }

    try {
      const appt = await medplum.readResource('Appointment', appointmentId);
      if (appt.status !== 'booked') {
        setError('This appointment cannot be rescheduled because it is not currently booked.');
        setLoading(false);
        return;
      }
      setAppointment(appt);
    } catch {
      setError('Appointment not found.');
    } finally {
      setLoading(false);
    }
  }, [medplum, appointmentId]);

  useEffect(() => {
    loadAppointment().catch(console.error);
  }, [loadAppointment]);

  useEffect(() => {
    if (!appointment || typesLoading) {
      return;
    }
    const typeCode = appointment.appointmentType?.coding?.[0]?.code;
    if (typeCode) {
      const found = appointmentTypes.find((t) => t.coding.code === typeCode);
      setAppointmentType(found);
    }
  }, [appointment, appointmentTypes, typesLoading]);

  async function handleConfirm(): Promise<void> {
    if (!appointmentId || !selectedCandidate || !profile || !appointmentType) {
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      if (profile) {
        await ensurePatientTimezone(medplum, profile);
      }
      await reschedulePatientV2Appointment(medplum, {
        appointmentId,
        patient: profile,
        appointmentType,
        candidate: selectedCandidate,
      });

      setShowConfirmation(false);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while rescheduling.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (loading || typesLoading) {
    return (
      <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Stack align="center" py="xl">
            <Loader />
            <Text c="dimmed">Loading appointment...</Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (error && !selectedCandidate) {
    return (
      <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Anchor component={Link} to="/appointments" size="sm">
            Appointments &gt;
          </Anchor>
          <Title order={2} mt="xs">
            Reschedule Appointment
          </Title>
        </Paper>
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Alert color="red">{error}</Alert>
        </Paper>
      </Stack>
    );
  }

  // Success screen
  const successSlot = selectedCandidate
    ? {
        start: selectedCandidate.appointment.start ?? '',
        end: selectedCandidate.appointment.end ?? '',
        providerName: selectedCandidate.providerName,
      }
    : undefined;
  const selectedBooking = selectedCandidate;

  if (success && appointmentType && successSlot) {
    return (
      <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Anchor component={Link} to="/appointments" size="sm">
            Appointments &gt;
          </Anchor>
          <Title order={2} mt="xs">
            Reschedule Appointment
          </Title>
        </Paper>
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Stack gap="md" align="center" py="xl">
            <IconCalendarCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>Appointment Rescheduled!</Title>
            <Card withBorder p="lg" radius="md" maw={400} w="100%">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Type
                  </Text>
                  <Text size="sm">{appointmentType.display}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    New Date
                  </Text>
                  <Text size="sm">{formatAppointmentDate(successSlot.start)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    New Time
                  </Text>
                  <AppointmentTimeWithTimezone
                    start={successSlot.start}
                    end={successSlot.end}
                    textProps={{ size: 'sm' }}
                  />
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Provider
                  </Text>
                  <Text size="sm">{successSlot.providerName}</Text>
                </Group>
              </Stack>
            </Card>
            <Text size="sm" c="dimmed" ta="center">
              Your previous appointment has been cancelled. A new confirmation will be sent.
            </Text>
            <Button variant="outline" onClick={() => navigate('/appointments')}>
              Back to Appointments
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  // Slot selection
  if (appointment && appointmentType) {
    return (
      <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Anchor component={Link} to="/appointments" size="sm">
            Appointments &gt;
          </Anchor>
          <Title order={2} mt="xs">
            Reschedule Appointment
          </Title>
        </Paper>

        <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
          <Card withBorder p="md" radius="md" mb="lg">
            <Text size="sm" fw={500} mb="xs">
              Current Appointment
            </Text>
            <Group gap="lg">
              <Text size="sm" c="dimmed">
                {appointmentType.display}
              </Text>
              <Text size="sm" c="dimmed">
                {formatAppointmentDate(appointment.start ?? '')}
              </Text>
              <AppointmentTimeWithTimezone
                start={appointment.start}
                end={appointment.end}
                textProps={{ size: 'sm', c: 'dimmed' }}
              />
            </Group>
          </Card>

          {error && (
            <Alert color="red" title="Reschedule failed" mb="md">
              {error}
            </Alert>
          )}

          <SlotPicker
            engine="medplum-v2"
            appointmentType={appointmentType}
            selectedSlot={selectedCandidate}
            onSlotSelected={setSelectedCandidate}
          />

          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => navigate('/appointments')}>
              Cancel
            </Button>
            <Button disabled={!selectedCandidate} onClick={() => setShowConfirmation(true)}>
              Reschedule Appointment
            </Button>
          </Group>

          {appointmentType && selectedBooking && (
            <BookingConfirmation
              opened={showConfirmation}
              appointmentType={appointmentType}
              slot={selectedBooking}
              onConfirm={handleConfirm}
              onBack={() => setShowConfirmation(false)}
              isSubmitting={isSubmitting}
            />
          )}
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="lg" bg="#f8f9fb" mih="100%">
      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <Text c="dimmed">Unable to load appointment details.</Text>
      </Paper>
    </Stack>
  );
}
